import { useMemo } from "react";
import { useServices } from "@core/servicesContext";
import { StorePresentation } from "@core/store/storePresentation";
import {
	AsyncResult,
	PaginatedDataResult,
	PromiseResult,
	useMappedStore,
	usePaginatedStore,
	usePromise,
	useStoredData,
} from "@utils/hooks";
import { Page } from "@utils/page";
import { useCurrentProfile } from "@domain/currentProfile/hooks";
import { Profile } from "@domain/user/profile";
import { Feedback } from "@domain/group/feedback/feedback";
import { Group, GroupAsset, GroupMemberRole, GroupType } from "./group";
import { DataChartFromDto } from "@domain/group/shareroom/dto";
import { Period } from "@domain/group/shareroom/shareroom";

export function useGroup(id: string, slug?: string): AsyncResult<Group> {
	const { groupService } = useServices();
	const currentProfile = useCurrentProfile();

	return useStoredData(slug ?? id, slug ? groupService.slugGroupStore : groupService.groupStore, [
		currentProfile?.id,
		id,
	]);
}

export function useMyDiscussions(): PaginatedDataResult<Group> {
	const { groupService } = useServices();
	return usePaginatedStore(groupService.userDiscussions, []);
}

export function useMySharerooms(): PaginatedDataResult<Group> {
	const currentProfile = useCurrentProfile();
	const { groupService } = useServices();
	return usePaginatedStore(groupService.mySharerooms, [currentProfile?.id]);
}
export function useMyGroupWithFilter(
	type: GroupType,
	memberRole?: GroupMemberRole | "ALL"
): PaginatedDataResult<Group> {
	const currentProfile = useCurrentProfile();
	const { groupService } = useServices();
	return usePaginatedStore(
		new StorePresentation(groupService.groupStore, (page) => groupService.getMyGroups(page, type, memberRole)),
		[type, memberRole, currentProfile?.id]
	);
}

export function useUserPublicSharerooms(profile: Profile): PaginatedDataResult<Group> {
	const { groupService } = useServices();
	return useMappedStore(profile.id, groupService.userSharerooms, [profile.id]);
}

export function useSuggestedGroups(count: number, groupType: GroupType): PaginatedDataResult<Group> {
	const { groupService } = useServices();
	const store = useMemo(
		() => new StorePresentation(groupService.groupStore, () => groupService.suggest(count, groupType)),
		[count, groupType]
	);
	return usePaginatedStore(store, [count, groupType]);
}

export function useActifMemberCount(groupId: string): PromiseResult<{ members: number }> {
	const { groupService } = useServices();
	return usePromise(() => groupService.getActifMemberCount(groupId), [groupId]);
}

export function useGroupAsset(count: number, groupId: string, type: string): PromiseResult<Page<GroupAsset>> {
	const { groupService } = useServices();
	const result = usePromise(() => groupService.getAssets(count, groupId, type), [groupId]);
	return result;
}

export function useSubscriptionsInPeriod(groupId: string, period: Period): PromiseResult<Array<DataChartFromDto>> {
	const { groupService } = useServices();

	return usePromise(() => groupService.getSubscriptionsInPeriod(groupId, period), [groupId, period]);
}

export function useShareroomFeedbacks(groupId: string): PaginatedDataResult<Feedback> {
	const { groupService } = useServices();
	const currentProfile = useCurrentProfile();
	return useMappedStore(groupId, groupService.feedbackStore, [groupId, currentProfile?.id]);
}
export function useShareroomLastFeedbacks(groupId: string): PromiseResult<Array<Feedback>> {
	const { groupService } = useServices();
	const currentProfile = useCurrentProfile();
	return usePromise(() => groupService.lastFeedbacks(groupId), [groupId, currentProfile?.id]);
}

export function useMyFeedback(groupId: string, feedbackId: string): PromiseResult<Feedback | null> {
	const { groupService } = useServices();
	return usePromise(() => groupService.getMyFeedback(groupId, feedbackId), [groupId, feedbackId]);
}
